/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { faEye, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import { ChangeEvent, useMemo, type FC } from 'react'
import { Button, ButtonGroup, Card, Col, Container, FloatingLabel, Form, Row, Stack } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

const validationSchema = yup.object().shape({
  id: yup.lazy(value => {
    if (typeof value === 'number') {
      return yup.number().required().nullable()
    }

    return yup.string().nullable()
  }),
  description: yup.string().min(1, 'A descrição deve ter no mínimo 1 caractere').max(255, 'A descrição deve ter no máximo 255 caracteres'),
  status: yup.string().oneOf(['active', 'inactive', 'deleted'], 'A situação deve ser ativo ou inativo').default('inactive'),
  publishedIn: yup.string().default(() => moment().format('YYYY-MM-DDTHH:mm')).matches(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'A data deve estar no formato YYYY-MM-DDTHH:mm'),
  unpublishedIn: yup.string().nullable().matches(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'A data deve estar no formato YYYY-MM-DDTHH:mm'),
  items: yup.array().of(yup.object().shape({
    description: yup.string().min(1, 'A descrição deve ter no mínimo 1 caractere').max(255, 'A descrição deve ter no máximo 255 caracteres'),
    status: yup.string().oneOf(['active', 'inactive', 'deleted'], 'A situação deve ser ativo ou inativo').default('inactive'),
    publishedIn: yup.string().default(() => moment().format('YYYY-MM-DDTHH:mm')).matches(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'A data deve estar no formato YYYY-MM-DDTHH:mm'),
    unpublishedIn: yup.string().nullable().matches(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'A data deve estar no formato YYYY-MM-DDTHH:mm'),
    url: yup.lazy(value => {
      if (value instanceof File) {
        return yup.mixed<File>().nullable().test('fileFormat', 'O arquivo deve ser do tipo PDF', value => {
          if (value instanceof File) {
            return value && ['application/pdf'].includes(value.type)
          }

          return true
        })
      }

      return yup.string().nullable()
    }),
  })).default([]).min(1, 'O relatório de transparência deve conter pelo menos um arquivo')
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormTransparencyReportHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormTransparencyReportHandleSubmit
  initialValues?: Partial<ValidationSchema>
  isLoading?: boolean
  readOnly?: boolean
}

/**
 * FormTransparencyReport is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormTransparencyReport from './src/views/PostEdit/FormTransparencyReport.tsx'
 *
 * const MyComponent = () => (
 *   <FormTransparencyReport />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormTransparencyReport: FC<Props> = ({ onSubmit = () => { }, initialValues = {}, isLoading = false, readOnly = false }) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  const createItem = () => {
    const items = formik.values.items || []
    formik.setFieldValue('items', [...items, {
      description: '',
      status: 'active',
      publishedIn: moment().format('YYYY-MM-DDTHH:mm'),
      unpublishedIn: null,
      url: null
    }])
  }

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Row>
        <Form.Group as={Col} md={8} className='mb-3'>
          <FloatingLabel label="Descrição do relatório (máx.: 255 caracteres)" className="mb-3">
            <Form.Control
              type='text'
              name='description'
              maxLength={255}
              value={formik?.values?.description ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly}
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={4} className='mb-3'>
          <FloatingLabel label="Visibilidade" className="mb-3">
            <Form.Select
              value={formik.values.status}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly}
              name="status"
            >
              <option>Selecione uma opção</option>
              <option value='active'>🌎 Pública</option>
              <option value='inactive'>🔒 Privada</option>
            </Form.Select>
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={6} className='mb-3'>
          <FloatingLabel label="Ativar em" className="mb-3">
            <Form.Control
              type='datetime-local'
              name='publishedIn'
              value={formik?.values?.publishedIn ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly}
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={6} className='mb-3'>
          <FloatingLabel label="Inativar em" className="mb-3">
            <Form.Control
              type='datetime-local'
              name='unpublishedIn'
              value={formik?.values?.unpublishedIn ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly}
            />
          </FloatingLabel>
        </Form.Group>
        <Col md={12}>
          <Container>
            <Col md={12} className='mb-3 mt-4'>
              <Stack direction='horizontal' gap={3}>
                <Card.Title as='div'>
                  <p style={{ fontWeight: 'bold', textTransform: 'uppercase', opacity: 0.7 }}>
                    Arquivos
                  </p>
                </Card.Title>
                <div className="p-2 ms-auto">
                  <Button size='sm' variant='primary' onClick={createItem}>
                    <FontAwesomeIcon icon={faPlus} />
                    {' '}
                    Adicionar arquivo
                  </Button>
                </div>
              </Stack>
            </Col>
            {formik?.values?.items.length === 0 && (
              <Col md={12} className='mb-3 mt-3' style={{ textAlign: 'center' }}>
                <p style={{ fontStyle: 'italic', opacity: 0.7 }}>Nenhum arquivo adicionado</p>
                <p style={{ fontStyle: 'italic', opacity: 0.7 }}>Clique no botão "Adicionar arquivo" para incluir um novo arquivo ao relatório de transparência</p>
              </Col>
            )}
            <Row style={{ flexDirection: 'column-reverse' }}>
              {formik?.values?.items?.map((item, index) => (
                <Col md={12} key={index}>
                  <Card style={{ border: '1px solid #cacaca' }}>
                    <Card.Header>
                      <Stack direction='horizontal' gap={3}>
                        <Card.Title as='div'>
                          <p style={{ fontWeight: 'bold' }}>
                            Arquivo {index + 1}
                          </p>
                        </Card.Title>
                        <div className="p-2 ms-auto">
                          <Button size='sm' variant='danger' onClick={() => {
                            const items = formik.values.items || []
                            formik.setFieldValue('items', items.filter((_, i) => i !== index))
                          }}>
                            <FontAwesomeIcon icon={faTrash} />
                            {' '}
                            Excluir arquivo
                          </Button>
                        </div>
                      </Stack>
                    </Card.Header>
                    <Card.Body>
                      <Container>
                        <Row>
                          <Form.Group as={Col} md={12} className='mb-3'>
                            <FloatingLabel label="Descrição do relatório (máx.: 255 caracteres)" className="mb-3">
                              <Form.Control
                                type='text'
                                name={`items[${index}].description`}
                                maxLength={255}
                                value={formik?.values?.items?.[index]?.description ?? ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={formik.isSubmitting || isLoading || readOnly}
                              />
                            </FloatingLabel>
                          </Form.Group>
                          <Form.Group as={Col} md={4} className='mb-3'>
                            <FloatingLabel label="Visibilidade" className="mb-3">
                              <Form.Select
                                value={formik?.values?.items?.[index]?.status ?? ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={formik.isSubmitting || isLoading || readOnly}
                                name={`items[${index}].status`}
                              >
                                <option>Selecione uma opção</option>
                                <option value='active'>🌎 Pública</option>
                                <option value='inactive'>🔒 Privada</option>
                              </Form.Select>
                            </FloatingLabel>
                          </Form.Group>
                          <Form.Group as={Col} md={4} className='mb-3'>
                            <FloatingLabel label="Ativar em" className="mb-3">
                              <Form.Control
                                type='datetime-local'
                                name={`items[${index}].publishedIn`}
                                value={formik?.values?.items?.[index]?.publishedIn ?? ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={formik.isSubmitting || isLoading || readOnly}
                              />
                            </FloatingLabel>
                          </Form.Group>
                          <Form.Group as={Col} md={4} className='mb-3'>
                            <FloatingLabel label="Inativar em" className="mb-3">
                              <Form.Control
                                type='datetime-local'
                                name={`items[${index}].unpublishedIn`}
                                value={formik?.values?.items?.[index]?.unpublishedIn ?? ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={formik.isSubmitting || isLoading || readOnly}
                              />
                            </FloatingLabel>
                          </Form.Group>
                          {item.url ? (
                            <Col md={12} className='mb-3'>
                              <ButtonGroup className="mb-2">
                                <Button
                                  size='sm'
                                  variant='link'
                                  onClick={() => {
                                    if (item.url instanceof File) {
                                      const fileURL = URL.createObjectURL(item.url)

                                      window.open(fileURL, '_blank', 'noopener,noreferrer')
                                      URL.revokeObjectURL(fileURL)
                                    }

                                    window.open(item.url?.toString(), '_blank', 'noopener,noreferrer')
                                  }}
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                  {' '}
                                  Pré-Visualizar
                                </Button>
                                <Button
                                  size='sm'
                                  variant='danger'
                                  onClick={() => {
                                    formik.setFieldValue(`items[${index}].url`, null)
                                  }}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                  {' '}
                                  Excluir anexo
                                </Button>
                              </ButtonGroup>
                            </Col>
                          ) : (
                            <Form.Group as={Col} md={12} className='mb-3'>
                              <FloatingLabel label="Anexo" className="mb-3">
                                <Form.Control
                                  name={`items[${index}].url`}
                                  type='file'
                                  accept="application/pdf"
                                  disabled={formik.isSubmitting || isLoading || readOnly}
                                  onBlur={formik.handleBlur}
                                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                    const file = event.target.files ? event.target.files[0] : null
                                    formik.setFieldValue(`items[${index}].url`, file)
                                  }}
                                />
                              </FloatingLabel>
                            </Form.Group>
                          )}
                        </Row>
                      </Container>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </Col>
        {Object.keys(formik.errors).map((key, index) => {
          const error = formik.errors[key as keyof typeof formik.errors]

          if (typeof error === 'string') {
            return (
              <Col md={12} key={index} className='mb-3 mt-3' style={{ textAlign: 'center' }}>
                <p style={{ fontStyle: 'italic', color: 'red' }}>{error}</p>
              </Col>
            )
          }
        })}
        <Col md={12}>
          <Button
            variant='outline-link'
            style={{ marginRight: 10 }}
            onClick={() => navigate('../')}
            disabled={formik.isSubmitting || isLoading}
          >
            Voltar
          </Button>
          <Button
            type='submit'
            disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}
          >
            Salvar
          </Button>
        </Col>
        {process.env.NODE_ENV === 'development' && (
          <Col md={12} style={{ marginTop: 20 }}>
            <Card>
              <Card.Header>Debug</Card.Header>
              <Card.Body>
                <pre>{JSON.stringify(formik.values, null, 2)}</pre>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </Form>
  )
}

export default FormTransparencyReport
